/**
 * =========================================================================================
 * SHERGOLD PRIME ERP - PRINT SYMBOLS & COINS CATALOG
 * =========================================================================================
 *
 * MODULE OVERVIEW:
 * Pre-configured unicode symbols, currency signs, bullion coin snippets, jewellery units,
 * contact glyphs, and tax prefixes designed for drag-and-drop or 1-click insertion into
 * invoice, receipt, voucher, and tag print templates.
 *
 * All glyphs in this catalog are verified for compatibility with pdfme / PDF generation
 * using standard TrueType / Roboto font rendering.
 * =========================================================================================
 */

export const POPULAR_QUICK_SYMBOLS = [
  { symbol: "₹", label: "Rupee (₹)", hint: "Indian Rupee currency sign" },
  { symbol: "🪙", label: "Coin (🪙)", hint: "Bullion gold / silver coin badge" },
  { symbol: "916", label: "916", hint: "22 Karat Hallmark purity" },
  { symbol: "750", label: "750", hint: "18 Karat Diamond purity" },
  { symbol: "999", label: "999", hint: "24 Karat Fine Gold" },
  { symbol: "g", label: "g", hint: "Weight in grams" },
  { symbol: "gm", label: "gm", hint: "Grams abbreviation" },
  { symbol: "ct", label: "ct", hint: "Carat diamond weight" },
  { symbol: "☎", label: "☎", hint: "Telephone icon" },
  { symbol: "✉", label: "✉", hint: "Email envelope" },
  { symbol: "📍", label: "📍", hint: "Store address pin" },
  { symbol: "%", label: "%", hint: "Percentage sign" },
  { symbol: "✓", label: "✓", hint: "Checkmark" },
  { symbol: "•", label: "•", hint: "Bullet dot delimiter" }
];

export const SYMBOL_CATEGORIES = [
  {
    id: "currency",
    name: "Rupee & Currencies",
    badge: "₹",
    description: "Indian Rupee signs, rate denominations, and Gulf / World currencies",
    items: [
      {
        symbol: "₹",
        label: "Indian Rupee (₹)",
        code: "INR",
        desc: "Official INR currency symbol",
        sampleSnippet: "₹ {{GrandTotal}}"
      },
      {
        symbol: "₹ ",
        label: "Rupee with Space (₹ )",
        code: "INR_SP",
        desc: "Rupee symbol with space for price formatting",
        sampleSnippet: "₹ 12,500.00"
      },
      {
        symbol: "(₹)",
        label: "In Brackets (₹)",
        code: "INR_PAREN",
        desc: "Denomination indicator for table column headers",
        sampleSnippet: "Total (₹)"
      },
      {
        symbol: "[₹]",
        label: "In Brackets [₹]",
        code: "INR_BRACKET",
        desc: "Square bracket currency denomination",
        sampleSnippet: "Rate [₹]"
      },
      {
        symbol: "Total (₹)",
        label: "Total (₹) Header",
        code: "TOTAL_INR",
        desc: "Standard table total column header",
        sampleSnippet: "Total (₹)"
      },
      {
        symbol: "Rate (₹)",
        label: "Rate (₹) Header",
        code: "RATE_INR",
        desc: "Standard table rate column header",
        sampleSnippet: "Rate (₹)"
      },
      {
        symbol: "₹/gm",
        label: "₹ per Gram (₹/gm)",
        code: "INR_PER_GM",
        desc: "Daily bullion rate per gram",
        sampleSnippet: "Rate: ₹/gm"
      },
      {
        symbol: "₹/g",
        label: "₹ per g (₹/g)",
        code: "INR_PER_G",
        desc: "Compact rate per gram",
        sampleSnippet: "₹/g"
      },
      {
        symbol: "₹/kg",
        label: "₹ per Kg (₹/kg)",
        code: "INR_PER_KG",
        desc: "Silver or bullion rate per kilogram",
        sampleSnippet: "₹/kg"
      },
      {
        symbol: "$",
        label: "US Dollar ($)",
        code: "USD",
        desc: "United States Dollar",
        sampleSnippet: "$ {{TotalAmount}}"
      },
      {
        symbol: "€",
        label: "Euro (€)",
        code: "EUR",
        desc: "European Union Euro",
        sampleSnippet: "€ {{TotalAmount}}"
      },
      {
        symbol: "£",
        label: "British Pound (£)",
        code: "GBP",
        desc: "British Pound Sterling",
        sampleSnippet: "£ {{TotalAmount}}"
      },
      {
        symbol: "¥",
        label: "Japanese Yen (¥)",
        code: "JPY",
        desc: "Japanese Yen / Chinese Yuan",
        sampleSnippet: "¥ {{TotalAmount}}"
      },
      {
        symbol: "AED",
        label: "UAE Dirham (AED)",
        code: "AED",
        desc: "United Arab Emirates Dirham (Middle East gold standard)",
        sampleSnippet: "AED {{TotalAmount}}"
      },
      {
        symbol: "SAR",
        label: "Saudi Riyal (SAR)",
        code: "SAR",
        desc: "Kingdom of Saudi Arabia Riyal",
        sampleSnippet: "SAR {{TotalAmount}}"
      },
      {
        symbol: "OMR",
        label: "Omani Rial (OMR)",
        code: "OMR",
        desc: "Sultanate of Oman Rial",
        sampleSnippet: "OMR {{TotalAmount}}"
      },
      {
        symbol: "KWD",
        label: "Kuwaiti Dinar (KWD)",
        code: "KWD",
        desc: "Kuwaiti Dinar",
        sampleSnippet: "KWD {{TotalAmount}}"
      },
      {
        symbol: "¢",
        label: "Cent (¢)",
        code: "CENT",
        desc: "Cent currency symbol",
        sampleSnippet: "¢"
      }
    ]
  },
  {
    id: "jewellery",
    name: "Coins, Bullion & Purity",
    badge: "🪙",
    description: "Gold / silver coin labels, BIS hallmark, weights, and karat standards",
    items: [
      {
        symbol: "🪙",
        label: "Coin Icon (🪙)",
        code: "COIN_ICO",
        desc: "Bullion and gold coin badge",
        sampleSnippet: "🪙 Gold Coin"
      },
      {
        symbol: "🪙 1g Gold Coin",
        label: "1g Gold Coin",
        code: "COIN_1G",
        desc: "1 Gram investment gold coin item label",
        sampleSnippet: "🪙 1g 24K Gold Coin"
      },
      {
        symbol: "🪙 2g Gold Coin",
        label: "2g Gold Coin",
        code: "COIN_2G",
        desc: "2 Gram investment gold coin item label",
        sampleSnippet: "🪙 2g 24K Gold Coin"
      },
      {
        symbol: "🪙 4g Gold Coin",
        label: "4g Gold Coin",
        code: "COIN_4G",
        desc: "4 Gram investment gold coin item label",
        sampleSnippet: "🪙 4g 24K Gold Coin"
      },
      {
        symbol: "🪙 8g Sovereign (1 Pavan)",
        label: "8g Sovereign (1 Pavan)",
        code: "COIN_8G",
        desc: "Traditional 8g (1 Sovereign / Pavan) gold coin standard",
        sampleSnippet: "🪙 8g Sovereign (1 Pavan)"
      },
      {
        symbol: "🪙 10g Gold Coin",
        label: "10g Gold Coin",
        code: "COIN_10G",
        desc: "10 Gram bullion gold coin label",
        sampleSnippet: "🪙 10g 24K 999 Coin"
      },
      {
        symbol: "🪙 Silver Coin",
        label: "Silver Coin / Bar",
        code: "COIN_SILVER",
        desc: "Pure silver gift coin or bullion bar",
        sampleSnippet: "🪙 999 Fine Silver Coin"
      },
      {
        symbol: "916",
        label: "916 Purity",
        code: "P916",
        desc: "22 Karat 91.6% hallmark gold purity",
        sampleSnippet: "Purity: 916"
      },
      {
        symbol: "22K 916",
        label: "22K 916 Hallmark",
        code: "P22K916",
        desc: "22 Karat 916 statutory jewellery hallmark",
        sampleSnippet: "22K 916 Hallmark"
      },
      {
        symbol: "750",
        label: "750 Purity",
        code: "P750",
        desc: "18 Karat 75.0% diamond jewellery purity",
        sampleSnippet: "Purity: 750"
      },
      {
        symbol: "18K 750",
        label: "18K 750 Hallmark",
        code: "P18K750",
        desc: "18 Karat 750 diamond jewellery hallmark",
        sampleSnippet: "18K 750 Diamond"
      },
      {
        symbol: "999",
        label: "999 Fine Gold",
        code: "P999",
        desc: "24 Karat 99.9% pure investment bullion",
        sampleSnippet: "24K 999 Pure"
      },
      {
        symbol: "999.9",
        label: "999.9 Fine Bullion",
        code: "P9999",
        desc: "999.9 Four-nine fine investment bullion",
        sampleSnippet: "999.9 Bullion"
      },
      {
        symbol: "925",
        label: "925 Sterling Silver",
        code: "P925",
        desc: "92.5% Sterling silver standard",
        sampleSnippet: "925 Sterling Silver"
      },
      {
        symbol: "585",
        label: "585 Purity (14K)",
        code: "P585",
        desc: "14 Karat gold purity standard",
        sampleSnippet: "14K (585)"
      },
      {
        symbol: "BIS 916 HALLMARKED",
        label: "BIS 916 Hallmarked",
        code: "BIS_HALLMARK",
        desc: "Statutory Bureau of Indian Standards hallmark badge",
        sampleSnippet: "BIS 916 HALLMARKED"
      },
      {
        symbol: "HUID: ",
        label: "HUID: Prefix",
        code: "HUID_PREFIX",
        desc: "Mandatory 6-character laser hallmark unique ID prefix",
        sampleSnippet: "HUID: {{HUID}}"
      },
      {
        symbol: "HUID: {{HUID}}",
        label: "HUID Dynamic Field",
        code: "HUID_TOKEN",
        desc: "Dynamic HUID laser token field",
        sampleSnippet: "HUID: {{HUID}}"
      },
      {
        symbol: "g",
        label: "Gram (g)",
        code: "UNIT_G",
        desc: "Standard metric gram unit for jewellery scale weight",
        sampleSnippet: "Gross: 5.420g"
      },
      {
        symbol: "gm",
        label: "Grams (gm)",
        code: "UNIT_GM",
        desc: "Grams abbreviation commonly printed in invoices",
        sampleSnippet: "Net Wt: 4.850 gm"
      },
      {
        symbol: "mg",
        label: "Milligram (mg)",
        code: "UNIT_MG",
        desc: "Milligrams for precision stone and bead weights",
        sampleSnippet: "Stone: 120 mg"
      },
      {
        symbol: "kg",
        label: "Kilogram (kg)",
        code: "UNIT_KG",
        desc: "Kilogram unit for bullion and silver bars",
        sampleSnippet: "Weight: 1.000 kg"
      },
      {
        symbol: "ct",
        label: "Carat (ct)",
        code: "UNIT_CT",
        desc: "Gemological carat unit (1 ct = 0.200 g) for diamonds",
        sampleSnippet: "Diamond: 0.45 ct"
      },
      {
        symbol: "kt",
        label: "Karat (kt)",
        code: "UNIT_KT",
        desc: "Gold purity karat unit",
        sampleSnippet: "22kt"
      },
      {
        symbol: "Gross Wt: ",
        label: "Gross Wt: Prefix",
        code: "GROSS_WT",
        desc: "Ornament gross weight label",
        sampleSnippet: "Gross Wt: {{GrossWeight}} g"
      },
      {
        symbol: "Net Wt: ",
        label: "Net Wt: Prefix",
        code: "NET_WT",
        desc: "Net billable gold weight label",
        sampleSnippet: "Net Wt: {{NetWeight}} g"
      },
      {
        symbol: "Pure Wt: ",
        label: "Pure Wt: Prefix",
        code: "PURE_WT",
        desc: "Calculated 24K pure gold weight label",
        sampleSnippet: "Pure Wt: {{PureWeight}} g"
      },
      {
        symbol: "Stone Wt: ",
        label: "Stone Wt: Prefix",
        code: "STONE_WT",
        desc: "Deducted gemstone weight label",
        sampleSnippet: "Stone Wt: {{StoneWeight}} g"
      },
      {
        symbol: "Touch: ",
        label: "Touch % Prefix",
        code: "TOUCH_PREFIX",
        desc: "Melting assay and touch purity percentage",
        sampleSnippet: "Touch: {{Touch}}%"
      },
      {
        symbol: "VA%: ",
        label: "VA% Prefix",
        code: "VA_PREFIX",
        desc: "Value addition / wastage charge percentage",
        sampleSnippet: "VA: {{VA_Percent}}%"
      },
      {
        symbol: "MC: ",
        label: "MC: Prefix",
        code: "MC_PREFIX",
        desc: "Artisan making charges prefix",
        sampleSnippet: "MC: ₹ {{MakingCharges}}"
      }
    ]
  },
  {
    id: "contact",
    name: "Contacts, Store & Tax IDs",
    badge: "☎",
    description: "Telephone, mobile, location, GSTIN, PAN, and banking identifiers",
    items: [
      {
        symbol: "☎",
        label: "Telephone (☎)",
        code: "PHONE_TEL",
        desc: "Landline phone icon for showroom header",
        sampleSnippet: "☎ 0484-2345678"
      },
      {
        symbol: "📞",
        label: "Phone Handset (📞)",
        code: "PHONE_HANDSET",
        desc: "Handset phone symbol",
        sampleSnippet: "📞 +91 9876543210"
      },
      {
        symbol: "📱",
        label: "Mobile (📱)",
        code: "MOBILE",
        desc: "Mobile phone symbol",
        sampleSnippet: "📱 +91 {{CompanyMobile}}"
      },
      {
        symbol: "✉",
        label: "Email (✉)",
        code: "EMAIL",
        desc: "Email mail envelope icon",
        sampleSnippet: "✉ {{CompanyEmail}}"
      },
      {
        symbol: "📍",
        label: "Location Pin (📍)",
        code: "LOCATION",
        desc: "Map marker icon for store address",
        sampleSnippet: "📍 {{CompanyAddress}}"
      },
      {
        symbol: "🌐",
        label: "Website (🌐)",
        code: "WEB",
        desc: "Website / web portal globe icon",
        sampleSnippet: "🌐 www.shergold.com"
      },
      {
        symbol: "GSTIN: ",
        label: "GSTIN: Prefix",
        code: "GSTIN_PREFIX",
        desc: "Goods and Services Tax Identification Number",
        sampleSnippet: "GSTIN: {{CompanyGSTIN}}"
      },
      {
        symbol: "PAN: ",
        label: "PAN: Prefix",
        code: "PAN_PREFIX",
        desc: "Permanent Account Number prefix",
        sampleSnippet: "PAN: {{CompanyPAN}}"
      },
      {
        symbol: "CIN: ",
        label: "CIN: Prefix",
        code: "CIN_PREFIX",
        desc: "Corporate Identity Number prefix",
        sampleSnippet: "CIN: {{CompanyCIN}}"
      },
      {
        symbol: "A/C: ",
        label: "A/C: Prefix",
        code: "AC_PREFIX",
        desc: "Bank account number prefix for RTGS/NEFT payment details",
        sampleSnippet: "A/C: {{BankAccountNo}}"
      },
      {
        symbol: "IFSC: ",
        label: "IFSC: Prefix",
        code: "IFSC_PREFIX",
        desc: "Bank IFSC routing code prefix",
        sampleSnippet: "IFSC: {{BankIFSC}}"
      },
      {
        symbol: "UPI: ",
        label: "UPI: Prefix",
        code: "UPI_PREFIX",
        desc: "Unified Payments Interface handle",
        sampleSnippet: "UPI: {{UPI_ID}}"
      },
      {
        symbol: "Ph: ",
        label: "Ph: Text",
        code: "PH_TEXT",
        desc: "Compact text abbreviation for phone",
        sampleSnippet: "Ph: {{CompanyPhone}}"
      },
      {
        symbol: "Mob: ",
        label: "Mob: Text",
        code: "MOB_TEXT",
        desc: "Compact text abbreviation for mobile",
        sampleSnippet: "Mob: {{CompanyMobile}}"
      }
    ]
  },
  {
    id: "typography",
    name: "Math, Badges & Dividers",
    badge: "%",
    description: "Percentage signs, delimiters, verification checkmarks, and stars",
    items: [
      {
        symbol: "%",
        label: "Percent (%)",
        code: "PCT",
        desc: "Percentage sign for GST and discount calculations",
        sampleSnippet: "GST 3%"
      },
      {
        symbol: "±",
        label: "Plus-Minus (±)",
        code: "PLUS_MINUS",
        desc: "Scale weight tolerance sign",
        sampleSnippet: "± 0.010 g"
      },
      {
        symbol: "+",
        label: "Plus (+)",
        code: "PLUS",
        desc: "Plus addition sign",
        sampleSnippet: "+ ₹ 500"
      },
      {
        symbol: "-",
        label: "Minus (-)",
        code: "MINUS",
        desc: "Minus deduction sign",
        sampleSnippet: "- ₹ {{Discount}}"
      },
      {
        symbol: "×",
        label: "Multiply / By (×)",
        code: "TIMES",
        desc: "Dimension and multiplication sign",
        sampleSnippet: "2 × ₹ 6,950"
      },
      {
        symbol: "•",
        label: "Bullet Point (•)",
        code: "BULLET",
        desc: "Bullet list separator dot",
        sampleSnippet: "• Terms & Conditions"
      },
      {
        symbol: "✓",
        label: "Checkmark (✓)",
        code: "CHECK",
        desc: "Verification check symbol",
        sampleSnippet: "✓ Verified Hallmark"
      },
      {
        symbol: "✔",
        label: "Heavy Check (✔)",
        code: "CHECK_HEAVY",
        desc: "Prominent check tick mark",
        sampleSnippet: "✔ Paid in Full"
      },
      {
        symbol: "★",
        label: "Solid Star (★)",
        code: "STAR",
        desc: "Gold premium star icon",
        sampleSnippet: "★ Certified Diamond"
      },
      {
        symbol: "☆",
        label: "Outline Star (☆)",
        code: "STAR_OUTLINE",
        desc: "Rating star outline",
        sampleSnippet: "☆"
      },
      {
        symbol: "|",
        label: "Divider Pipe (|)",
        code: "PIPE",
        desc: "Vertical delimiter between store metadata",
        sampleSnippet: "Ph: 9876543210 | GSTIN: 32AABCS"
      },
      {
        symbol: "—",
        label: "Em Dash (—)",
        code: "EM_DASH",
        desc: "Horizontal line separator dash",
        sampleSnippet: "—"
      },
      {
        symbol: "№",
        label: "Numero Sign (№)",
        code: "NUMERO",
        desc: "Voucher / Bill number indicator",
        sampleSnippet: "Bill № {{InvoiceNo}}"
      },
      {
        symbol: "GST 3%",
        label: "GST 3% Tag",
        code: "GST_3PCT",
        desc: "Statutory jewellery GST rate",
        sampleSnippet: "GST @ 3%"
      },
      {
        symbol: "CGST 1.5%",
        label: "CGST 1.5% Tag",
        code: "CGST_15PCT",
        desc: "Central GST rate for intra-state supply",
        sampleSnippet: "CGST 1.5%"
      },
      {
        symbol: "SGST 1.5%",
        label: "SGST 1.5% Tag",
        code: "SGST_15PCT",
        desc: "State GST rate for intra-state supply",
        sampleSnippet: "SGST 1.5%"
      },
      {
        symbol: "IGST 3.0%",
        label: "IGST 3.0% Tag",
        code: "IGST_30PCT",
        desc: "Integrated GST rate for inter-state supply",
        sampleSnippet: "IGST 3.0%"
      }
    ]
  }
];
